// src/screens/DailyMetrics.js
// Daily metrics dashboard. Reads from api.sanoire.com/api/admin-metrics
// (gated by ADMIN_API_SECRET). 30-day window with totals + per-day rows.

import React, { useState, useEffect } from 'react';
import { Card, CardTitle, Grid, MetricCard, Insight } from '../components/UI';

const F = 'system-ui,-apple-system,"Helvetica Neue",Arial,sans-serif';
const fmt = (n) => (n == null ? '—' : Number(n).toLocaleString());
const fmtUSD = (n) => (n == null ? '—' : '$' + Number(n).toLocaleString(undefined, { maximumFractionDigits: 0 }));

// Admin secret is configured at build time via REACT_APP_ADMIN_API_SECRET.
// In dev mode, the screen will show the request as 401 if the secret isn't set.
const ADMIN_SECRET = process.env.REACT_APP_ADMIN_API_SECRET || '';
const API_BASE = process.env.REACT_APP_API_BASE || 'https://api.sanoire.com';

export default function DailyMetrics() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const res = await fetch(`${API_BASE}/api/admin-metrics`, {
          headers: { 'x-admin-secret': ADMIN_SECRET },
        });
        if (!res.ok) {
          if (!cancelled) setErr(`HTTP ${res.status}`);
          return;
        }
        const j = await res.json();
        if (!cancelled) setData(j);
      } catch (e) {
        if (!cancelled) setErr(e.message || 'fetch_failed');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, []);

  if (loading) {
    return (
      <div style={{ padding: 22, fontFamily: F, color: '#4e7f97' }}>
        Loading metrics…
      </div>
    );
  }

  if (err || !data || !data.ok) {
    return (
      <div style={{ padding: 22 }}>
        <Card>
          <CardTitle>Cannot load metrics</CardTitle>
          <div style={{ fontFamily: F, color: '#9c4a3a', fontSize: 13 }}>
            {err || data?.error || 'Unknown error.'}
          </div>
          <div style={{ fontFamily: F, color: '#7a8e98', fontSize: 12, marginTop: 8 }}>
            Make sure REACT_APP_ADMIN_API_SECRET is set in the sanoire-admin
            Vercel env and matches ADMIN_API_SECRET on sanoire-api.
          </div>
        </Card>
      </div>
    );
  }

  const totals = data.metrics_30d?.totals || {};
  const days = data.metrics_30d?.days || [];
  const topSubids = data.top_affiliate_subids || [];
  const flagged = data.flagged_bookings || [];

  return (
    <div style={{ padding: 22 }}>
      <h1 style={{ fontFamily: F, fontSize: 24, fontWeight: 600, color: '#0f2a40', marginTop: 0 }}>
        Daily Metrics
      </h1>
      <Insight tone="info" style={{ marginBottom: 18 }}>
        Rolling 30-day view. Aggregated nightly at 01:00 UTC by the
        <code style={{ fontFamily: 'ui-monospace,Menlo,monospace', background: '#edf2f5', padding: '2px 6px', borderRadius: 4, margin: '0 4px' }}>aggregate-daily-metrics</code>
        Vercel cron.
      </Insight>

      <Grid>
        <MetricCard label="New users · 30d" value={fmt(totals.new_users_30d)} />
        <MetricCard label="Bookings · 30d" value={fmt(totals.bookings_30d)} />
        <MetricCard label="GMV · 30d" value={fmtUSD(totals.gmv_30d)} />
        <MetricCard label="Sana queries · 30d" value={fmt(totals.sana_queries_30d)} />
        <MetricCard label="Affiliate clicks · 30d" value={fmt(totals.affiliate_30d)} />
      </Grid>

      <Card style={{ marginTop: 18 }}>
        <CardTitle>Per-day · last 30 days</CardTitle>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', fontFamily: F, fontSize: 13, color: '#0f2a40', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ textAlign: 'left', color: '#7a8e98', fontSize: 11 }}>
                <th style={{ padding: '8px 6px' }}>Day</th>
                <th style={{ padding: '8px 6px', textAlign: 'right' }}>New users</th>
                <th style={{ padding: '8px 6px', textAlign: 'right' }}>Bookings</th>
                <th style={{ padding: '8px 6px', textAlign: 'right' }}>GMV</th>
                <th style={{ padding: '8px 6px', textAlign: 'right' }}>Sana queries</th>
                <th style={{ padding: '8px 6px', textAlign: 'right' }}>Affiliate clicks</th>
              </tr>
            </thead>
            <tbody>
              {days.length === 0 && (
                <tr><td colSpan={6} style={{ padding: '12px 6px', color: '#7a8e98', fontStyle: 'italic' }}>
                  No daily snapshots yet — the cron writes its first row at 01:00 UTC.
                </td></tr>
              )}
              {days.map(d => (
                <tr key={d.day} style={{ borderTop: '1px solid #eef2f5' }}>
                  <td style={{ padding: '8px 6px' }}>{d.day}</td>
                  <td style={{ padding: '8px 6px', textAlign: 'right' }}>{fmt(d.new_users)}</td>
                  <td style={{ padding: '8px 6px', textAlign: 'right' }}>{fmt(d.bookings)}</td>
                  <td style={{ padding: '8px 6px', textAlign: 'right' }}>{fmtUSD(d.gmv)}</td>
                  <td style={{ padding: '8px 6px', textAlign: 'right' }}>{fmt(d.sana_queries)}</td>
                  <td style={{ padding: '8px 6px', textAlign: 'right' }}>{fmt(d.affiliate_clicks)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <div style={{ marginTop: 18, display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(380px,1fr))', gap: 14 }}>
        <Card>
          <CardTitle>Top affiliate sub_ids · 7d</CardTitle>
          {topSubids.length === 0 ? (
            <div style={{ fontFamily: F, color: '#7a8e98', fontSize: 13 }}>
              No clicks logged yet. They'll appear here once the native app
              starts routing through <code>trackAffiliateClick</code>.
            </div>
          ) : (
            <table style={{ width: '100%', fontFamily: F, fontSize: 13, color: '#0f2a40' }}>
              <thead>
                <tr style={{ textAlign: 'left', color: '#7a8e98', fontSize: 11 }}>
                  <th style={{ padding: '6px 4px' }}>Partner</th>
                  <th style={{ padding: '6px 4px' }}>Sub_id</th>
                  <th style={{ padding: '6px 4px', textAlign: 'right' }}>Clicks</th>
                  <th style={{ padding: '6px 4px', textAlign: 'right' }}>Users</th>
                </tr>
              </thead>
              <tbody>
                {topSubids.map((r, i) => (
                  <tr key={i} style={{ borderTop: '1px solid #eef2f5' }}>
                    <td style={{ padding: '6px 4px' }}>{r.partner}</td>
                    <td style={{ padding: '6px 4px', fontFamily: 'ui-monospace,Menlo,monospace' }}>{r.sub_id || '—'}</td>
                    <td style={{ padding: '6px 4px', textAlign: 'right' }}>{fmt(r.click_count)}</td>
                    <td style={{ padding: '6px 4px', textAlign: 'right' }}>{fmt(r.unique_users)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>

        <Card>
          <CardTitle>Flagged bookings · last 25</CardTitle>
          {flagged.length === 0 ? (
            <div style={{ fontFamily: F, color: '#5d8a5e', fontSize: 13 }}>
              No flagged bookings in the velocity log.
            </div>
          ) : (
            <table style={{ width: '100%', fontFamily: F, fontSize: 12, color: '#0f2a40' }}>
              <thead>
                <tr style={{ textAlign: 'left', color: '#7a8e98', fontSize: 11 }}>
                  <th style={{ padding: '6px 4px' }}>When</th>
                  <th style={{ padding: '6px 4px' }}>Type</th>
                  <th style={{ padding: '6px 4px', textAlign: 'right' }}>Amount</th>
                  <th style={{ padding: '6px 4px' }}>Decision</th>
                </tr>
              </thead>
              <tbody>
                {flagged.map(b => (
                  <tr key={b.id} style={{ borderTop: '1px solid #eef2f5' }}>
                    <td style={{ padding: '6px 4px' }}>{new Date(b.created_at).toLocaleString()}</td>
                    <td style={{ padding: '6px 4px' }}>{b.booking_type}</td>
                    <td style={{ padding: '6px 4px', textAlign: 'right' }}>{fmtUSD(b.attempted_amount_usd)}</td>
                    <td style={{ padding: '6px 4px', color: b.decision === 'block' ? '#9c4a3a' : '#c4a86b' }}>
                      {b.decision}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>
      </div>
    </div>
  );
}
